import { PageViewTracker } from "@/main/common/PageViewTracker";
import { ClientFactory } from "@/main/common/ClientFactory";
import { consumeFeedClickOrigin } from "@/main/analytics/feedClickOrigin";
import type { CountDiscussionViewRequest } from "@/main/common/DiscussionViewTypes";

export class DiscussionViewTracker {
    private readonly tracker: PageViewTracker;
    private readonly discussionPublicId: string;

    /**
     * The resolved click origin. `undefined` = not looked up yet; `null` = looked up, there was none.
     */
    private resolvedRequest: CountDiscussionViewRequest | null | undefined = undefined;

    constructor(discussionPublicId: string) {
        this.discussionPublicId = discussionPublicId;
        this.tracker = new PageViewTracker({
            errorMessage: "Failed to record discussion view",
            context: { discussionPublicId },
            trackView: () => ClientFactory.countDiscussionView(
                discussionPublicId,
                this.resolveAttributionOnce(),
            ),
        });
    }

    /**
     * Reads the single-read click-origin slot LAZILY — on the first actual `count_view` attempt —
     * rather than in the constructor, and caches the answer.
     *
     * <p>Both halves matter. Lazily, because `DiscussionContentDisplayer` builds the tracker inside a
     * `useEffect`: under StrictMode that effect runs twice, so a constructor-time read would have
     * tracker A consume the single-read slot and tracker B — the one that actually fires — send
     * nothing. Cached, because `PageViewTracker` retries on 5xx/408/429 and a second read of a
     * single-read store returns nothing, so an uncached retry would post a different body than the
     * attempt it is retrying.
     *
     * <p>Returns `undefined` when there is no origin, so `ClientFactory` omits both fields and the
     * server stores `NULL` — "unattributed" — instead of a false `direct`.
     */
    private resolveAttributionOnce(): CountDiscussionViewRequest | undefined {
        if (this.resolvedRequest === undefined) {
            const origin = consumeFeedClickOrigin(this.discussionPublicId);
            this.resolvedRequest = origin
                ? {
                    source: origin.source,
                    ...(origin.sourceDetail ? { sourceDetail: origin.sourceDetail } : {}),
                }
                : null;
        }

        return this.resolvedRequest ?? undefined;
    }

    public startViewTracking(): void {
        this.tracker.startViewTracking();
    }

    public stopViewTracking(): void {
        this.tracker.stopViewTracking();
    }

}
